<template>
    <Layout>
        <div class="row">
            <div class="col-lg-12">
                <div class="card">
                    <div class="card-header">
                        <h3 class="mb-0 title--screen">抽選新規登録</h3>
                    </div>
                    <div class="card-body">
                        <!-- Step 1: Choose type -->
                        <div v-if="!registrationType" class="text-center py-5">
                            <button class="btn btn-info btn-lg m-3 px-5 py-3" @click="selectType('internal')">既存フォームから登録</button>
                            <br />
                            <button class="btn btn-info btn-lg m-3 px-5 py-3" @click="selectType('external')">外部ファイルから登録</button>
                        </div>

                        <!-- Step 2: Tabs -->
                        <div v-else>
                            <ul class="nav nav-tabs mb-4">
                                <li class="nav-item">
                                    <a
                                        class="nav-link font-weight-bold"
                                        :class="{ active: currentTab === 'basic' }"
                                        href="javascript:void(0)"
                                        @click="currentTab = 'basic'"
                                    >
                                        基本情報登録
                                    </a>
                                </li>
                                <li class="nav-item">
                                    <a
                                        class="nav-link font-weight-bold"
                                        :class="{ active: currentTab === 'setting', disabled: !lotteryId }"
                                        href="javascript:void(0)"
                                        @click="lotteryId && (currentTab = 'setting')"
                                    >
                                        抽選設定
                                    </a>
                                </li>
                                <li v-if="checkPermission('lottery.analytics')" class="nav-item">
                                    <a
                                        class="nav-link font-weight-bold"
                                        :class="{ active: currentTab === 'result', disabled: !lotteryId }"
                                        href="javascript:void(0)"
                                        @click="lotteryId && (currentTab = 'result')"
                                    >
                                        抽選結果
                                    </a>
                                </li>
                            </ul>

                            <!-- TAB 1: Basic Info -->
                            <div v-if="currentTab === 'basic'">
                                <div class="form-group row">
                                    <label class="col-sm-2 col-form-label">タイトル <span class="text-danger">*</span></label>
                                    <div class="col-sm-10">
                                        <input
                                            v-model="form.title"
                                            type="text"
                                            class="form-control"
                                            :class="{ 'is-invalid': errors.title }"
                                            placeholder=""
                                        />
                                        <div v-if="errors.title" class="invalid-feedback" style="display: block">{{ errors.title }}</div>
                                    </div>
                                </div>
                                <div v-if="registrationType === 'internal'" class="form-group row">
                                    <label class="col-sm-2 col-form-label">フォーム選択 <span class="text-danger">*</span></label>
                                    <div class="col-sm-10">
                                        <Multiselect
                                            id="select-form"
                                            :value.sync="form.form_id"
                                            :options="forms"
                                            :config="{ trackBy: 'id', label: 'name' }"
                                            :class="{ 'is-invalid': errors.form_id }"
                                        />
                                        <div v-if="errors.form_id" class="invalid-feedback" style="display: block">{{ errors.form_id }}</div>
                                    </div>
                                </div>
                                <div v-if="registrationType === 'external'" class="form-group row">
                                    <label class="col-sm-2 col-form-label">ファイルアップロード <span class="text-danger">*</span></label>
                                    <div class="col-sm-10">
                                        <UploadsFiles
                                            id="upload-file-external"
                                            :title="'ファイルアップロード'"
                                            classLabel="btn-secondary"
                                            typeUpload="import"
                                            :config="{ allowedExtension: ['csv', 'xlsx'], allowedSize: 204800 }"
                                            @importFile="handleFileUpload"
                                        />
                                        <small class="text-muted" style="font-size: 13px">※アップロード可能なファイル形式：.csv, .xlsx</small>
                                        <div v-if="errors.file" class="invalid-feedback" style="display: block">{{ errors.file }}</div>
                                    </div>
                                </div>
                                <div class="form-group row">
                                    <label class="col-sm-2 col-form-label">登録タグ</label>
                                    <div class="col-sm-10">
                                        <KeyWordTag id="registration-tags" checkPre="hash" :model.sync="form.tags" :config.sync="configKeyword" />
                                    </div>
                                </div>
                                <div class="text-center mt-4">
                                    <button type="button" class="btn btn-outline-secondary px-4" @click="registrationType = null">キャンセル</button>
                                    <button type="button" class="btn btn-info px-4 ml-3" :disabled="isLoadingBasic" @click="submitBasicInfo">
                                        <span v-if="isLoadingBasic"><i class="ri-loader-4-line"></i> 処理中...</span>
                                        <span v-else>登録</span>
                                    </button>
                                </div>
                            </div>

                            <!-- TAB 2: Setting -->
                            <div v-if="currentTab === 'setting'">
                                <SettingTab
                                    :lottery-id="lotteryId"
                                    :list-block-form="listBlockForm"
                                    :rewards.sync="rewards"
                                    :is-loading-setting="isLoadingSetting"
                                    @submit="submitSetting(false)"
                                />
                            </div>

                            <!-- TAB 3: Result -->
                            <div v-if="currentTab === 'result'">
                                <ResultTab
                                    ref="resultTab"
                                    :lottery-id="lotteryId"
                                    :list-block-form="listBlockForm"
                                    :rewards.sync="rewards"
                                    :post-draw-config.sync="globalPostDrawDuplicateConfig"
                                    :full-data-dup.sync="globalFullDataDuplicateEnabled"
                                    @submit-then-draw="handleSubmitThenDraw"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </Layout>
</template>

<script>
import Layout from '@/views/layouts/main';
import appConfig from '@/app.config';
import KeyWordTag from '@/components/KeywordTag/main.vue';
import UploadsFiles from '@/components/Uploads/Files/main.vue';
import Multiselect from '@/components/Multiselect/main.vue';
import SettingTab from '../components/SettingTab.vue';
import ResultTab from '../components/ResultTab.vue';
import { managerFormMethods, lotteryMethods } from '@/state/helpers';
import { showMessage } from '@/utils/messages';
import { checkPermission } from '@/utils/handlers';

export default {
    page: {
        title: '抽選新規登録',
        meta: [{ name: 'description', content: appConfig.description }]
    },
    components: {
        UploadsFiles,
        Layout,
        KeyWordTag,
        Multiselect,
        SettingTab,
        ResultTab
    },
    data() {
        return {
            registrationType: null,
            currentTab: 'basic',
            lotteryId: null,

            // Basic form
            form: { title: '', form_id: null, file: null, tags: [] },
            errors: {},
            configKeyword: { arrayTag: [], placeholder: '', isShowLable: false, isCheckDuplicate: true },
            forms: [],
            isLoadingBasic: false,

            // Shared between tabs
            rewards: [],
            listBlockForm: [],
            isLoadingSetting: false,
            globalPostDrawDuplicateConfig: null,
            globalFullDataDuplicateEnabled: false
        };
    },
    created() {
        this.fetchForms();
    },
    watch: {
        'form.title'(val) {
            if (!val) {
                this.$set(this.errors, 'title', 'タイトルを入力してください。');
            } else if (val.length > 255) {
                this.$set(this.errors, 'title', 'タイトルを255文字以内で入力してください。');
            } else {
                this.$delete(this.errors, 'title');
            }
        },
        'form.form_id'(val) {
            if (this.registrationType === 'internal' && !val) {
                this.$set(this.errors, 'form_id', 'フォームを選択してください。');
            } else {
                this.$delete(this.errors, 'form_id');
            }
        },
        'form.file'(val) {
            if (this.registrationType === 'external' && !val) {
                this.$set(this.errors, 'file', 'ファイルをアップロードしてください。');
            } else {
                this.$delete(this.errors, 'file');
            }
        },
        registrationType() {
            this.$delete(this.errors, 'form_id');
            this.$delete(this.errors, 'file');
        }
    },
    methods: {
        ...managerFormMethods,
        ...lotteryMethods,
        checkPermission,

        async fetchForms() {
            try {
                const res = await this.getListManagerForm('page=1&limit=9999&sorts[id]=desc');
                if (res && res.data) this.forms = Array.isArray(res.data) ? res.data : res.data.data || [];
            } catch (e) {
                console.error(e);
            }
        },

        selectType(type) {
            this.registrationType = type;
            this.currentTab = 'basic';
        },

        handleFileUpload(file) {
            this.form.file = file;
        },

        async submitBasicInfo() {
            this.errors = {};
            if (!this.form.title) this.errors.title = 'タイトルを入力してください。';
            else if (this.form.title.length > 255) this.errors.title = 'タイトルを255文字以内で入力してください。';
            if (this.registrationType === 'internal' && !this.form.form_id) this.errors.form_id = 'フォームを選択してください。';
            if (this.registrationType === 'external' && !this.form.file) this.errors.file = 'ファイルをアップロードしてください。';

            if (Object.keys(this.errors).length > 0) return;

            this.isLoadingBasic = true;
            try {
                const tags = this.configKeyword.arrayTag && this.configKeyword.arrayTag.length > 0 ? this.configKeyword.arrayTag : this.form.tags;
                if (this.registrationType === 'internal') {
                    const payload = {
                        title: this.form.title,
                        source_type: 'form',
                        form_id: this.form.form_id?.id || this.form.form_id,
                        tags: tags
                    };
                    const res = await this.registerLottery(payload);
                    this.lotteryId = res?.data?.id || res?.id;

                    if (payload.form_id) {
                        const blocks = await this.getListBlockOfForm({ id: payload.form_id, query: 'exclude_deleted=true' });
                        this.listBlockForm = (blocks || [])
                            .sort((a, b) => a.order_index - b.order_index)
                            .map((b) => ({
                                ...b,
                                options: b.list_options,
                                submit_results: []
                            }));
                    }
                    this.isLoadingBasic = false;
                    this.$router.push({ path: '/lottery/list', query: { created: 'true' } });
                } else {
                    const formData = new FormData();
                    formData.append('title', this.form.title);
                    formData.append('source_type', 'external');
                    tags.forEach((t, i) => formData.append(`tags[${i}]`, t));
                    formData.append('file', this.form.file);

                    const res = await this.acRegisterExternalLottery(formData);
                    const data = res?.data || res;
                    this.lotteryId = data.lottery ? data.lottery.id : data.id;
                    const importId = data.import?.id || data.import_id;

                    if (importId) {
                        this.pollImportStatus(importId);
                    } else {
                        await this.fetchExternalColumns();
                        this.isLoadingBasic = false;
                        this.$router.push({ path: '/lottery/list', query: { created: 'true' } });
                    }
                }
            } catch (e) {
                console.error(e);
                this.isLoadingBasic = false;
            }
        },

        pollImportStatus(importId) {
            const timer = setInterval(async () => {
                try {
                    const res = await this.acGetExternalImportStatus({ lotteryId: this.lotteryId, importId });
                    const status = res?.data?.status || res?.status;
                    if (status === 'active' || status === 'success' || status === 'completed') {
                        clearInterval(timer);
                        showMessage(200, this.$bvToast, '抽選が作成されました。');
                        await this.fetchExternalColumns();
                        this.isLoadingBasic = false;
                        this.$router.push('/lottery/list');
                    } else if (status === 'failed' || status === 'error') {
                        clearInterval(timer);
                        this.isLoadingBasic = false;
                        showMessage(500, this.$bvToast, 'ファイルのインポートに失敗しました');
                    }
                } catch (e) {
                    console.error(e);
                    clearInterval(timer);
                    this.isLoadingBasic = false;
                }
            }, 3000);
        },

        async fetchExternalColumns() {
            try {
                const res = await this.acGetExternalDataColumns(this.lotteryId);
                const columns = res?.data || res || [];
                this.listBlockForm = columns.map((c) => ({
                    id: c,
                    block_name: c,
                    block_type: 'input',
                    options: [],
                    submit_results: []
                }));
            } catch (e) {
                console.error(e);
            }
        },

        parseConditionQuery(queryString) {
            if (!queryString) return { condition: 'AND', filter_groups: [] };

            let condition = 'AND';
            const params = new URLSearchParams(queryString);
            const groups = {};

            for (const [key, val] of params.entries()) {
                if (key === 'searchCriteria[filterGroups][condition]') {
                    condition = val;
                } else {
                    const match = key.match(/searchCriteria\[filterGroups\]\[(\d+)\]\[([^\]]+)\]\[(\d+)\]\[([^\]]+)\]/);
                    if (match) {
                        const groupIdx = parseInt(match[1]);
                        const arrayName = match[2];
                        const itemIdx = parseInt(match[3]);
                        const prop = match[4];

                        if (!groups[groupIdx]) groups[groupIdx] = {};
                        if (!groups[groupIdx][arrayName]) groups[groupIdx][arrayName] = [];
                        if (!groups[groupIdx][arrayName][itemIdx]) groups[groupIdx][arrayName][itemIdx] = {};

                        const finalVal = val === 'true' ? true : val === 'false' ? false : val;

                        if (key.endsWith('[]')) {
                            if (!groups[groupIdx][arrayName][itemIdx][prop]) {
                                groups[groupIdx][arrayName][itemIdx][prop] = [];
                            }
                            groups[groupIdx][arrayName][itemIdx][prop].push(finalVal);
                        } else {
                            groups[groupIdx][arrayName][itemIdx][prop] = finalVal;
                        }
                    }
                }
            }

            const filterGroupsArr = Object.keys(groups)
                .sort((a, b) => a - b)
                .map((k) => {
                    const group = groups[k];
                    const res = {};
                    if (group.filter_blocks) res.filter_blocks = group.filter_blocks.filter(Boolean);
                    if (group.filter_result) res.filter_result = group.filter_result.filter(Boolean);
                    return res;
                });

            return { condition, filter_groups: filterGroupsArr };
        },

        async submitSetting(preventSwitch = false) {
            if (!this.lotteryId || !this.rewards.length) return false;
            this.isLoadingSetting = true;
            try {
                const payload = {
                    reward_config: {
                        rewards: this.rewards.map((r) => {
                            const conditionParsed =
                                r.condition_config ||
                                (r.conditionQuery ? this.parseConditionQuery(r.conditionQuery) : { condition: 'AND', filter_groups: [] });
                            return {
                                text: r.text,
                                quantity: r.quantity,
                                condition_config: conditionParsed,
                                pre_draw_duplicate_config: { fields: r.preDrawFields.filter((f) => f?.id).map((f) => String(f.id)) },
                                post_draw_duplicate_config: {
                                    enabled: r.postDrawFields.some((f) => f?.id),
                                    fields: r.postDrawFields.filter((f) => f?.id).map((f) => String(f.id))
                                },
                                full_data_duplicate_enabled: r.fullDataDuplicateEnabled
                            };
                        })
                    }
                };
                await this.settingLottery({ id: this.lotteryId, data: payload });
                if (!preventSwitch) {
                    this.currentTab = 'result';
                    this.$nextTick(() => {
                        if (this.$refs.resultTab) {
                            this.$refs.resultTab.selectReward(this.$refs.resultTab.selectedRewardIdx || 0);
                        }
                    });
                }
                return true;
            } catch (e) {
                console.error(e);
                return false;
            } finally {
                this.isLoadingSetting = false;
            }
        },

        async handleSubmitThenDraw(rewardIndex) {
            if (this.$refs.resultTab) {
                this.$refs.resultTab.executeDraw(rewardIndex);
            }
        }
    }
};
</script>
